import { useState, useEffect } from 'react';
import {
  Currency,
  Table,
  Td,
  TableRow,
  Th,
  THead,
  TableWrapper,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
} from '@increasecard/typed-components';
import { DownloadIcon } from '@increasecard/icons';
import styled from 'styled-components';
import { MainContent, ContentWrapper } from '../components/Containers';
import SectionTitle from '../components/SectionTitle';
import { getInvoices, getInvoiceDownloadLink, PaginatedInvoices } from '../services/BillingService';
import LoadingIndicator from '../components/LoadingIndicator';
import { User, UserSubscription, useUser } from '@increasecard/increase-core';
import { Paginator } from '../components/Paginator';

const PayUsersCaption = styled.p`
  margin: 30px auto;
`;

const DownloadButton = styled.button`
  color: ${({ theme }) => theme.colors.brandBlack.regular};
  border: none;
  background: none;
  cursor: pointer;
  hover {
    color: red;
  }
`;

const resolveUsablePaySubscription = (user: User | null) => {
  if (!user) return false;
  const paySubscription = user.subscriptions.find(
    (sub: UserSubscription) => sub.product.name === 'pay',
  );
  if (!paySubscription) return false;
  return paySubscription.state === 'awaiting_setup' || paySubscription.state === 'usable';
};

const Invoices = () => {
  const [invoices, setInvoices] = useState<PaginatedInvoices | null>(null);
  const [downloadModal, setDownloadModal] = useState(false);
  const [page, setPage] = useState(1);
  const user = useUser();
  const hasUsablePaySubscription = resolveUsablePaySubscription(user);

  function downloadInvoice(id: string) {
    return () => {
      setDownloadModal(true);
      getInvoiceDownloadLink(id).then((link) => {
        window.open(link);
        setDownloadModal(false);
      });
    };
  }

  useEffect(() => {
    getInvoices(page).then(setInvoices);
  }, [page]);

  const loadingInvoices = invoices === null || user === null;

  return (
    <ContentWrapper>
      <MainContent>
        <SectionTitle>Tus Facturas</SectionTitle>
        {hasUsablePaySubscription && (
          <PayUsersCaption>
            Las facturas emitidas por el servicio de Pay llegarán al correo electrónico registrado
            al momento de adquirir la Suscripción. Si necesitas modificar este correo electrónico o
            tienes alguna consulta comunícate con nuestro equipo de
            <Link
              forwardedAs="a"
              href="https://soporte.increasecard.com/hc/es/requests/new?ticket_form_id=360003356153"
              style={{ margin: '0 5px' }}
              type="button"
            >
              Atención al Cliente
            </Link>
          </PayUsersCaption>
        )}
        {loadingInvoices ? (
          <LoadingIndicator />
        ) : invoices?.data.length === 0 ? (
          <p>No hay facturas disponibles</p>
        ) : (
          <>
            <TableWrapper>
              <Table zebra>
                <THead>
                  <TableRow>
                    <Th>Razón Social</Th>
                    <Th>Nº de Factura</Th>
                    <Th>Fecha de emisión</Th>
                    <Th>Vencimiento</Th>
                    <Th rightAligned>Importe</Th>
                    <Th rightAligned>Descargar</Th>
                  </TableRow>
                </THead>
                <tbody>
                  {invoices?.data.map(
                    ({
                      number,
                      companyLegalName,
                      displayEmissionDate,
                      displayExpirationDate,
                      currency,
                      amount,
                      id,
                    }) => (
                      <TableRow double key={number}>
                        <Td>{companyLegalName}</Td>
                        <Td>{number}</Td>
                        <Td>{displayEmissionDate}</Td>
                        <Td>{displayExpirationDate}</Td>
                        <Td rightAligned>
                          <Currency currency={currency} language="es-AR" value={amount} />
                        </Td>
                        <Td rightAligned>
                          <DownloadButton onClick={downloadInvoice(id)}>
                            <DownloadIcon height="24" width="24" />
                          </DownloadButton>
                        </Td>
                      </TableRow>
                    ),
                  )}
                </tbody>
              </Table>
              {invoices?.data.length > 0 && (
                <Paginator
                  align="right"
                  onSelectPage={setPage}
                  pages={invoices?.total_pages}
                  selected={invoices ? Number(invoices?.current_page) : 0}
                />
              )}
            </TableWrapper>
            <Modal
              width="500"
              align="center"
              onClose={() => setDownloadModal(false)}
              visible={downloadModal}
              onOk={() => {
                return;
              }}
              onCancel={() => {
                return;
              }}
            >
              <ModalHeader>Preparando descarga...</ModalHeader>
              <ModalContent>
                <LoadingIndicator size={32} />
              </ModalContent>
            </Modal>
          </>
        )}
      </MainContent>
    </ContentWrapper>
  );
};

export default Invoices;
